import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { getVisitFrequencySummary } from "../../services";
import { getBranchCode } from "../../services/keycloak";
import { featureAccess } from "../../permissions/access";
import { useUserRole } from "../../utils/hooks/useUserRole";
import {
  NO_ROUTE_PARAM,
  VisitBucketKey,
  VisitFrequencyRoute,
  VisitFrequencySummaryResponse,
} from "../../types/visit-frequency.type";
import DateRangePicker, { DATE_PRESETS, DateRangePreset } from "../common/DateRangePicker";
import FilterField, { FILTER_ROW_BOTTOM_SPACING } from "../SaleDepositReport/FilterField";
import { downloadBlob } from "../SalesReportExport/utils";
import CountsTable, { CountsTableRow } from "./CountsTable";
import CsvExportButton from "./CsvExportButton";
import CustomerTable, { ALL_OPTION } from "./CustomerTable";
import HolidayPicker from "./HolidayPicker";
import KpiCards from "./KpiCards";
import VisitReportTabs from "./ReportTabs";
import { VISIT_FREQUENCY_PATH } from "./reportPaths";
import {
  API_DATE,
  BUCKETS_BY_MODE,
  MAX_RANGE_DAYS,
  MIN_FREQUENCY_WORKING_DAYS,
  buildCountsCsv,
  csvBlob,
  formatCount,
  isBucketKey,
  isRangeTooLong,
  normalizeHolidays,
  parseHolidayParam,
  routeLabel,
} from "./visitFrequency";

dayjs.locale("th");

type Level = "branches" | "routes" | "shops";

const PARAM = {
  dateFrom: "date_from",
  dateTo: "date_to",
  holidays: "holidays",
  level: "level",
  branch: "branch",
  route: "route",
  bucket: "bucket",
} as const;

/**
 * No year-long presets: the API caps a range at 92 days, and ไตรมาสนี้ is the widest
 * shortcut that always fits. A hand-picked longer span is blocked with a message below.
 */
const PRESET_GROUPS: DateRangePreset[][] = [
  [DATE_PRESETS.today, DATE_PRESETS.yesterday, DATE_PRESETS.last7],
  [DATE_PRESETS.thisWeek, DATE_PRESETS.lastWeek, DATE_PRESETS.last30],
  [DATE_PRESETS.thisMonth, DATE_PRESETS.lastMonth, DATE_PRESETS.thisQuarter],
];

const parseDate = (value: string | null): Dayjs | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = dayjs(value);
  return d.isValid() ? d.startOf("day") : null;
};

/**
 * สรุปรอบเข้าเยี่ยมร้าน — how often each shop was visited in a range, replacing finance's
 * monthly Excel summary. Three levels, all addressed by the URL so the browser's back button
 * walks back up them: every branch → one branch by สาย → the shops behind a count.
 *
 * Head office opens at every branch. A branch-scoped reader opens straight on their own
 * branch; the server pins them to it whatever the URL says, so the branch in the URL is a
 * convenience, not the guard.
 */
export default function VisitFrequency() {
  const role = useUserRole();
  const isCrossBranch = featureAccess.visitFrequencyAllBranches(role);
  const tokenBranch = useMemo(() => getBranchCode(), []);
  const [searchParams, setSearchParams] = useSearchParams();

  const [defaultFrom, defaultTo] = useMemo(() => DATE_PRESETS.lastMonth.range(), []);
  const dateFrom = parseDate(searchParams.get(PARAM.dateFrom)) ?? defaultFrom;
  const dateTo = parseDate(searchParams.get(PARAM.dateTo)) ?? defaultTo;
  const holidays = normalizeHolidays(
    parseHolidayParam(searchParams.get(PARAM.holidays)),
    dateFrom,
    dateTo,
  );

  const branch = isCrossBranch
    ? searchParams.get(PARAM.branch)
    : searchParams.get(PARAM.branch) ?? tokenBranch;
  const requestedLevel = searchParams.get(PARAM.level) as Level | null;
  const level: Level = !branch
    ? "branches"
    : requestedLevel === "shops"
      ? "shops"
      : "routes";
  const route = searchParams.get(PARAM.route) ?? ALL_OPTION;
  const bucketParam = searchParams.get(PARAM.bucket);
  const bucket = bucketParam === ALL_OPTION || isBucketKey(bucketParam) ? bucketParam : "none";

  const updateParams = useCallback(
    (patch: Partial<Record<(typeof PARAM)[keyof typeof PARAM], string | null>>, push = false) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") next.delete(key);
            else next.set(key, value);
          });
          return next;
        },
        { replace: !push },
      );
    },
    [setSearchParams],
  );

  const apiFrom = dateFrom.format(API_DATE);
  const apiTo = dateTo.format(API_DATE);
  const holidayKey = holidays.join(",");
  const tooLong = isRangeTooLong(dateFrom, dateTo);
  const apiBranch = level === "branches" ? undefined : branch ?? undefined;

  const [summary, setSummary] = useState<VisitFrequencySummaryResponse>();
  const [error, setError] = useState(false);
  // The request the current `summary`/`error` answers. Anything else on screen is stale — the
  // render right after a drill-down still holds the previous level's data, and must not show
  // it (or mount the shop list against it) before the new answer arrives.
  const requestKey = [apiFrom, apiTo, holidayKey, apiBranch ?? ""].join("|");
  const [answeredKey, setAnsweredKey] = useState<string | null>(null);
  const loading = !tooLong && answeredKey !== requestKey;

  const latestKey = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (tooLong) {
      latestKey.current = null;
      setSummary(undefined);
      setError(false);
      return;
    }
    const key = [apiFrom, apiTo, holidayKey, apiBranch ?? ""].join("|");
    latestKey.current = key;
    try {
      const response = await getVisitFrequencySummary({
        date_from: apiFrom,
        date_to: apiTo,
        holidays: holidayKey ? holidayKey.split(",") : [],
        branch_code: apiBranch,
      });
      // Only the latest request may settle the page; an older, slower one is dropped.
      if (latestKey.current !== key) return;
      setSummary(response.data.data ?? undefined);
      setError(false);
    } catch {
      if (latestKey.current !== key) return;
      setError(true);
      setSummary(undefined);
    }
    setAnsweredKey(key);
  }, [apiFrom, apiTo, holidayKey, apiBranch, tooLong]);

  useEffect(() => {
    load();
  }, [load]);

  const onChangeRange = useCallback(
    (from: Dayjs, to: Dayjs) =>
      updateParams({
        [PARAM.dateFrom]: from.format(API_DATE),
        [PARAM.dateTo]: to.format(API_DATE),
        // Holidays outside the new range mean nothing; drop them rather than send them.
        [PARAM.holidays]: normalizeHolidays(holidays, from, to).join(",") || null,
      }),
    [updateParams, holidays],
  );

  const onChangeHolidays = (next: string[]) =>
    updateParams({ [PARAM.holidays]: next.join(",") || null });

  const openBranch = (branchCode: string) =>
    updateParams({ [PARAM.branch]: branchCode, [PARAM.level]: "routes", [PARAM.route]: null, [PARAM.bucket]: null }, true);

  const openShops = (patch: { branch?: string; route?: string; bucket?: string }) =>
    updateParams(
      {
        [PARAM.level]: "shops",
        [PARAM.branch]: patch.branch ?? branch,
        [PARAM.route]: patch.route ?? null,
        [PARAM.bucket]: patch.bucket ?? null,
      },
      true,
    );

  const goToAllBranches = () =>
    updateParams({ [PARAM.branch]: null, [PARAM.level]: null, [PARAM.route]: null, [PARAM.bucket]: null }, true);

  const goToRoutes = () =>
    updateParams({ [PARAM.level]: "routes", [PARAM.route]: null, [PARAM.bucket]: null }, true);

  // The one branch a branch-level response is about.
  // Matched by code for head office; a branch-scoped reader only ever gets their own row, and
  // the token's branch_code need not be spelled the way the API spells it.
  const currentBranch =
    level === "branches"
      ? undefined
      : summary?.branches.find((b) => b.branch_code === branch) ??
        (!isCrossBranch && summary?.branches.length === 1 ? summary.branches[0] : undefined);
  const branchLabel = currentBranch
    ? `${currentBranch.branch_code} ${currentBranch.branch_name}`
    : branch ?? "";
  const routes: VisitFrequencyRoute[] = currentBranch?.routes ?? [];
  const scopeCounts = level === "branches" ? summary?.totals : currentBranch;

  const rangeText = `${apiFrom}_${apiTo}`;
  const preamble = summary
    ? [
        ["ช่วงวันที่", `${summary.date_from} ถึง ${summary.date_to}`],
        ["วันทำงาน", summary.working_days],
        ...(summary.holidays.length ? [["วันหยุดเพิ่มเติม", summary.holidays.join(" ")]] : []),
      ]
    : [];

  const exportBranches = () => {
    if (!summary) return;
    const csv = buildCountsCsv({
      leadHeaders: ["ลำดับ", "รหัสสาขา", "ชื่อสาขา", "จำนวนสาย"],
      rows: summary.branches.map((b, index) => ({
        cells: [index + 1, b.branch_code, b.branch_name, b.route_count],
        counts: b,
      })),
      total: { cells: ["รวม", "", "", summary.branches.reduce((n, b) => n + b.route_count, 0)], counts: summary.totals },
      mode: summary.mode,
      thresholds: summary.thresholds,
      workingDays: summary.working_days,
      preamble,
    });
    downloadBlob(`visit-frequency_all-branches_${rangeText}.csv`, csvBlob(csv));
  };

  const exportRoutes = () => {
    if (!summary || !currentBranch) return;
    const csv = buildCountsCsv({
      leadHeaders: ["ลำดับ", "สาย"],
      rows: routes.map((r, index) => ({ cells: [index + 1, routeLabel(r)], counts: r })),
      total: { cells: ["รวม", ""], counts: currentBranch },
      mode: summary.mode,
      thresholds: summary.thresholds,
      workingDays: summary.working_days,
      preamble: [["สาขา", branchLabel], ...preamble],
    });
    downloadBlob(`visit-frequency_${currentBranch.branch_code}_routes_${rangeText}.csv`, csvBlob(csv));
  };

  const branchRows: CountsTableRow[] = (summary?.branches ?? []).map((b, index) => ({
    key: b.company_id,
    cells: [index + 1, b.branch_code, b.branch_name, formatCount(b.route_count)],
    counts: b,
    onOpen: () => openBranch(b.branch_code),
    onBucketClick: (bucketKey: VisitBucketKey) => openShops({ branch: b.branch_code, bucket: bucketKey }),
  }));

  const routeRows: CountsTableRow[] = routes.map((r, index) => {
    const routeParam = r.route_number ?? NO_ROUTE_PARAM;
    return {
      key: routeParam,
      cells: [index + 1, routeLabel(r)],
      counts: r,
      muted: r.shops === 0,
      onOpen: () => openShops({ route: routeParam, bucket: ALL_OPTION }),
      onBucketClick: (bucketKey: VisitBucketKey) => openShops({ route: routeParam, bucket: bucketKey }),
    };
  });

  const offDays = summary ? dateTo.diff(dateFrom, "day") + 1 - summary.working_days : 0;

  return (
    <Box sx={{ padding: "8px 0" }}>
      <VisitReportTabs current={VISIT_FREQUENCY_PATH} />

      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
        {isCrossBranch &&
          (level === "branches" ? (
            <Typography color="text.primary">ทุกสาขา</Typography>
          ) : (
            <Link component="button" underline="hover" onClick={goToAllBranches}>
              ทุกสาขา
            </Link>
          ))}
        {level !== "branches" &&
          (level === "routes" ? (
            <Typography color="text.primary">{branchLabel}</Typography>
          ) : (
            <Link component="button" underline="hover" onClick={goToRoutes}>
              {branchLabel}
            </Link>
          ))}
        {level === "shops" && <Typography color="text.primary">รายชื่อร้าน</Typography>}
      </Breadcrumbs>

      <Grid container spacing={2} mb={FILTER_ROW_BOTTOM_SPACING} alignItems="center">
        <FilterField label="วันที่">
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            onChange={onChangeRange}
            presetGroups={PRESET_GROUPS}
          />
        </FilterField>
        <FilterField label="วันหยุด">
          <HolidayPicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            holidays={holidays}
            onChange={onChangeHolidays}
            disabled={tooLong}
          />
        </FilterField>
        <Grid size={{ xs: 12, md: 6 }}>
          {summary && !loading && (
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Chip color="primary" label={`วันทำงาน ${formatCount(summary.working_days)} วัน`} />
              <Typography variant="caption" color="text.secondary">
                ไม่นับวันอาทิตย์{summary.holidays.length > 0 ? "และวันหยุดเพิ่มเติม" : ""}{" "}
                {formatCount(offDays)} วัน
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {tooLong && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          เลือกช่วงวันที่ได้ไม่เกิน {MAX_RANGE_DAYS} วัน (ช่วงที่เลือก{" "}
          {dateTo.diff(dateFrom, "day") + 1} วัน) — กรุณาเลือกช่วงให้สั้นลง
        </Alert>
      )}

      {summary?.mode === "visited_only" && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          ช่วงที่เลือกมีวันทำงานน้อยกว่า {MIN_FREQUENCY_WORKING_DAYS} วัน
          จึงแบ่งรอบการเข้า (ทุกวัน / วันเว้นวัน / อาทิตย์ละ 2 ครั้ง / อาทิตย์ละครั้ง) ไม่ได้ —
          แสดงเฉพาะ เข้าร้าน / ไม่เข้าร้าน
        </Alert>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          โหลดข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && summary && level !== "branches" && !currentBranch && (
        <Alert severity="warning">ไม่พบข้อมูลของสาขา {branch}</Alert>
      )}

      {!loading && summary && scopeCounts && (
        <>
          <KpiCards
            counts={scopeCounts}
            mode={summary.mode}
            thresholds={summary.thresholds}
            workingDays={summary.working_days}
            onBucketClick={
              currentBranch ? (bucketKey) => openShops({ bucket: bucketKey }) : undefined
            }
          />

          {level !== "branches" && currentBranch && (
            <Tabs
              value={level}
              onChange={(_, next: Level) => (next === "routes" ? goToRoutes() : openShops({}))}
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              <Tab value="routes" label="สรุปรายสาย" />
              <Tab value="shops" label="รายชื่อร้าน" />
            </Tabs>
          )}

          {level === "branches" && (
            <CountsTable
              title="สรุปรายสาขา"
              subtitle="คลิกแถวเพื่อดูรายสาย · คลิกตัวเลขเพื่อดูรายชื่อร้าน"
              action={<CsvExportButton onExport={exportBranches} disabled={summary.branches.length === 0} />}
              leadColumns={[
                { header: "ลำดับ", align: "right" },
                { header: "รหัสสาขา" },
                { header: "ชื่อสาขา", minWidth: 120 },
                { header: "จำนวนสาย", align: "right" },
              ]}
              rows={branchRows}
              total={{ label: "รวม", counts: summary.totals }}
              mode={summary.mode}
              thresholds={summary.thresholds}
              workingDays={summary.working_days}
            />
          )}

          {level === "routes" && currentBranch && (
            <CountsTable
              title={`สรุปรายสาย — ${branchLabel}`}
              subtitle={`${formatCount(currentBranch.route_count)} สาย · คลิกแถวเพื่อดูร้านในสาย · คลิกตัวเลขเพื่อดูรายชื่อร้าน`}
              action={<CsvExportButton onExport={exportRoutes} disabled={routes.length === 0} />}
              leadColumns={[{ header: "ลำดับ", align: "right" }, { header: "สาย", minWidth: 160 }]}
              rows={routeRows}
              total={{ label: "รวม", counts: currentBranch }}
              mode={summary.mode}
              thresholds={summary.thresholds}
              workingDays={summary.working_days}
              emptyText="สาขานี้ไม่มีสาย"
            />
          )}

          {level === "shops" && currentBranch && (
            <CustomerTable
              dateFrom={apiFrom}
              dateTo={apiTo}
              holidays={holidays}
              branchCode={currentBranch.branch_code}
              branchLabel={branchLabel}
              mode={summary.mode}
              routes={routes}
              route={route}
              onRouteChange={(next) => updateParams({ [PARAM.route]: next === ALL_OPTION ? null : next })}
              bucket={
                // A bucket from the other mode (e.g. ทุกวัน on a 3-day range) has no option.
                bucket === ALL_OPTION || BUCKETS_BY_MODE[summary.mode].includes(bucket as VisitBucketKey)
                  ? bucket
                  : "none"
              }
              onBucketChange={(next) => updateParams({ [PARAM.bucket]: next })}
            />
          )}
        </>
      )}
    </Box>
  );
}
